import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radar, TrendingUp, FileText, Zap, RefreshCw, ArrowRight, AlertTriangle, Clock, Sparkles } from "lucide-react";

const kpiPalette = [
  { tile: "bg-indigo-100 text-indigo-600",   ring: "ring-indigo-200",   grad: "from-indigo-500 to-violet-500" },
  { tile: "bg-emerald-100 text-emerald-600", ring: "ring-emerald-200", grad: "from-emerald-500 to-teal-500" },
  { tile: "bg-fuchsia-100 text-fuchsia-600", ring: "ring-fuchsia-200",  grad: "from-fuchsia-500 to-pink-500" },
  { tile: "bg-amber-100 text-amber-600",     ring: "ring-amber-200",    grad: "from-amber-500 to-orange-500" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [top, setTop] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const [s, o, d] = await Promise.all([
        api.get("/pipeline/stats"),
        api.get("/opportunities?min_score=0"),
        api.get("/notifications/deadlines"),
      ]);
      setStats(s.data);
      setTop(o.data.slice(0, 6));
      setDeadlines(d.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/opportunities/refresh");
      toast.success(`Loaded ${r.data.new} new opportunities from SAM.gov`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const kpis = [
    { label: "Total Opportunities", val: stats?.total ?? 0, icon: Radar },
    { label: "High Match (≥75)",    val: stats?.high_match ?? 0, icon: TrendingUp },
    { label: "Proposals",           val: stats?.proposals ?? 0, icon: FileText },
    { label: "Needs Review",        val: stats?.stages?.needs_review ?? 0, icon: Zap },
  ];

  return (
    <div className="p-8 stagger-in" data-testid="dashboard-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="bf-eyebrow text-violet-600">COMMAND CENTER</span>
            <Sparkles className="w-4 h-4 text-violet-400" />
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-slate-900">Mission <span className="bf-grad-text">Overview</span>.</h1>
          <p className="text-slate-500 mt-3 text-base">Live opportunity engine from SAM.gov · Ranked by fit</p>
        </div>
        <button onClick={refresh} disabled={refreshing} className="bf-pill px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="refresh-opportunities">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "SCANNING…" : "SCAN SAM.GOV"}
        </button>
      </div>

      {deadlines.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5 mb-8" data-testid="deadline-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-mono font-bold tracking-widest text-amber-700 mb-2">DEADLINE REMINDERS · {deadlines.length}</div>
                <div className="space-y-1.5">
                  {deadlines.slice(0, 4).map(d => (
                    <Link key={d.notice_id} to={`/opportunities/${d.notice_id}`} className="block text-sm hover:text-violet-600 transition-colors" data-testid={`deadline-${d.notice_id}`}>
                      <span className={`font-mono mr-2 font-semibold ${d.days_left < 0 ? "text-rose-600" : d.days_left < 2 ? "text-rose-500" : "text-amber-700"}`}>
                        {d.days_left < 0 ? `${Math.abs(Math.floor(d.days_left))}d OVERDUE` : `${d.days_left.toFixed(1)}d left`}
                      </span>
                      <span className="text-slate-700">{d.title}</span>
                      <span className="text-xs font-mono text-slate-500 ml-2">{d.agency}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link to="/bid-due" className="text-xs font-mono font-bold tracking-widest text-violet-600 hover:text-violet-700 flex items-center gap-2 shrink-0" data-testid="see-all-deadlines">
              <Clock className="w-3.5 h-3.5" />BID DUE RADAR
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          const p = kpiPalette[i];
          return (
            <div key={k.label} className={`rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300`} data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-xl ${p.tile} flex items-center justify-center`}>
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <span className="text-[10px] font-mono font-bold tracking-widest text-slate-400">STAT</span>
              </div>
              <div className={`font-display font-black text-4xl mb-1 bg-gradient-to-br ${p.grad} bg-clip-text text-transparent`}>{k.val}</div>
              <div className="text-xs text-slate-500 font-medium">{k.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-slate-100">
            <div>
              <div className="text-xs font-mono font-bold tracking-widest text-violet-600 mb-1">TOP MATCHES</div>
              <div className="font-display font-bold text-xl text-slate-900">Highest-scoring opportunities</div>
            </div>
            <Link to="/opportunities" className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-violet-600 transition-colors flex items-center gap-2" data-testid="view-all-opportunities">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {top.length === 0 && (
              <div className="p-10 text-center text-slate-500">
                <Radar className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                No opportunities yet. Click <span className="text-violet-600 font-semibold">Scan SAM.gov</span> to begin.
              </div>
            )}
            {top.map((o) => (
              <Link key={o.notice_id} to={`/opportunities/${o.notice_id}`} className="block p-5 hover:bg-violet-50/40 transition-colors" data-testid={`opp-row-${o.notice_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-mono tracking-wider text-slate-500 mb-1 truncate uppercase">{o.agency || "—"} · {o.state || o.place_of_performance || "—"}</div>
                    <div className="font-semibold text-slate-900 truncate">{o.title}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {o.naics && <span className="text-[10px] font-mono font-bold tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5">NAICS {o.naics}</span>}
                      {o.set_aside && <span className="text-[10px] font-mono font-bold tracking-wider text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5 truncate max-w-[220px]">{o.set_aside}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-display font-black text-3xl ${o.match_score >= 85 ? "text-emerald-600" : o.match_score >= 70 ? "text-amber-600" : "text-slate-400"}`}>{o.match_score}</div>
                    <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400">MATCH</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-white to-violet-50/50 border border-slate-200 p-6">
          <div className="text-xs font-mono font-bold tracking-widest text-fuchsia-600 mb-1">PIPELINE</div>
          <div className="font-display font-bold text-xl text-slate-900 mb-6">Stage breakdown</div>
          <div className="space-y-3">
            {stats && Object.entries(stats.stages).map(([stage, count]) => (
              <div key={stage} className="flex items-center justify-between">
                <span className="text-sm text-slate-700 capitalize">{stage.replace("_", " ")}</span>
                <span className="font-mono text-sm font-bold text-slate-900">{count}</span>
              </div>
            ))}
          </div>
          <Link to="/pipeline" className="mt-6 pt-6 border-t border-slate-200/60 text-xs font-mono font-bold tracking-widest text-violet-600 hover:text-violet-700 flex items-center gap-2" data-testid="view-pipeline">
            Open pipeline board <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
